import { Suspense, useState, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, useGLTF, Environment, PresentationControls } from "@react-three/drei";
import { Button } from "@/components/ui/button";
import { Maximize2, RotateCw, Loader2 } from "lucide-react";
import eap3dModel from "@/assets/eap-3d-model.png";
import { useLanguage } from "@/contexts/LanguageContext";

function Model() {
  const { scene } = useGLTF("/models/eap-abutment.glb");
  return <primitive object={scene} scale={0.4} />;
}

// Preload the model
useGLTF.preload("/models/eap-abutment.glb");

const translations = {
  de: {
    badge: "Interaktives 3D-Modell",
    title: "EAP® Abutment in 3D",
    subtitle: "Erkunden Sie unser patientenindividuelles Hybrid-Abutment im Detail",
    altText: "EAP® Abutment 3D Modell - Patientenindividuelles Hybrid-Abutment für Zahnimplantate",
    noWebGL: "Ihr Browser unterstützt keine 3D-Ansicht. Das Bild zeigt unser EAP® Abutment.",
    loadError: "3D-Modell konnte nicht geladen werden",
    retry: "Erneut versuchen",
    dragRotate: "Ziehen zum Drehen",
    scrollZoom: "Scrollen zum Zoomen",
    fullscreen: "Vollbild",
    feature1Title: "EAP®",
    feature1Text: "Goldfarbig anodisiert",
    feature2Title: "Titan",
    feature2Text: "Hochwertige Materialien",
    feature3Title: "Individuell",
    feature3Text: "Patientenspezifisch",
  },
  en: {
    badge: "Interactive 3D Model",
    title: "EAP® Abutment in 3D",
    subtitle: "Explore our patient-specific hybrid abutment in detail",
    altText: "EAP® Abutment 3D Model - Patient-specific hybrid abutment for dental implants",
    noWebGL: "Your browser does not support 3D view. The image shows our EAP® Abutment.",
    loadError: "3D model could not be loaded",
    retry: "Try again",
    dragRotate: "Drag to rotate",
    scrollZoom: "Scroll to zoom",
    fullscreen: "Fullscreen",
    feature1Title: "EAP®",
    feature1Text: "Gold-colored anodized",
    feature2Title: "Titanium",
    feature2Text: "High-quality materials",
    feature3Title: "Individual",
    feature3Text: "Patient-specific",
  },
  fr: {
    badge: "Modèle 3D interactif",
    title: "Pilier EAP® en 3D",
    subtitle: "Explorez notre pilier hybride personnalisé en détail",
    altText: "Modèle 3D du pilier EAP® - Pilier hybride personnalisé pour implants dentaires",
    noWebGL: "Votre navigateur ne prend pas en charge la vue 3D. L'image montre notre pilier EAP®.",
    loadError: "Le modèle 3D n'a pas pu être chargé",
    retry: "Réessayer",
    dragRotate: "Faire glisser pour pivoter",
    scrollZoom: "Défiler pour zoomer",
    fullscreen: "Plein écran",
    feature1Title: "EAP®",
    feature1Text: "Anodisé couleur or",
    feature2Title: "Titane",
    feature2Text: "Matériaux de haute qualité",
    feature3Title: "Individuel",
    feature3Text: "Spécifique au patient",
  },
  nl: {
    badge: "Interactief 3D-model",
    title: "EAP® Abutment in 3D",
    subtitle: "Verken ons patiëntspecifieke hybride abutment in detail",
    altText: "EAP® Abutment 3D-model - Patiëntspecifiek hybride abutment voor tandimplantaten",
    noWebGL: "Uw browser ondersteunt geen 3D-weergave. De afbeelding toont ons EAP® Abutment.",
    loadError: "3D-model kon niet worden geladen",
    retry: "Opnieuw proberen",
    dragRotate: "Slepen om te draaien",
    scrollZoom: "Scrollen om in te zoomen",
    fullscreen: "Volledig scherm",
    feature1Title: "EAP®",
    feature1Text: "Goudkleurig geanodiseerd",
    feature2Title: "Titanium",
    feature2Text: "Hoogwaardige materialen",
    feature3Title: "Individueel",
    feature3Text: "Patiëntspecifiek",
  },
  it: {
    badge: "Modello 3D interattivo",
    title: "Abutment EAP® in 3D",
    subtitle: "Esplora il nostro abutment ibrido personalizzato nel dettaglio",
    altText: "Modello 3D Abutment EAP® - Abutment ibrido personalizzato per impianti dentali",
    noWebGL: "Il tuo browser non supporta la visualizzazione 3D. L'immagine mostra il nostro Abutment EAP®.",
    loadError: "Il modello 3D non può essere caricato",
    retry: "Riprova",
    dragRotate: "Trascina per ruotare",
    scrollZoom: "Scorri per ingrandire",
    fullscreen: "Schermo intero",
    feature1Title: "EAP®",
    feature1Text: "Anodizzato color oro",
    feature2Title: "Titanio",
    feature2Text: "Materiali di alta qualità",
    feature3Title: "Individuale",
    feature3Text: "Specifico per il paziente",
  },
  es: {
    badge: "Modelo 3D interactivo",
    title: "Pilar EAP® en 3D",
    subtitle: "Explore nuestro pilar híbrido personalizado en detalle",
    altText: "Modelo 3D del pilar EAP® - Pilar híbrido personalizado para implantes dentales",
    noWebGL: "Su navegador no admite vista 3D. La imagen muestra nuestro pilar EAP®.",
    loadError: "El modelo 3D no se pudo cargar",
    retry: "Intentar de nuevo",
    dragRotate: "Arrastrar para rotar",
    scrollZoom: "Desplazar para hacer zoom",
    fullscreen: "Pantalla completa",
    feature1Title: "EAP®",
    feature1Text: "Anodizado color oro",
    feature2Title: "Titanio",
    feature2Text: "Materiales de alta calidad",
    feature3Title: "Individual",
    feature3Text: "Específico para el paciente",
  },
};

export const Model3DViewer = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [error, setError] = useState(false);
  const [webGLSupported, setWebGLSupported] = useState(true);

  useEffect(() => {
    // Check WebGL support
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) {
        setWebGLSupported(false);
        setError(true);
      }
    } catch (e) {
      setWebGLSupported(false);
      setError(true);
    }
  }, []);

  return (
    <section className="py-12 bg-gradient-to-b from-background to-secondary/20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 animate-fade-up">
          <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            {t.badge}
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden shadow-card bg-gradient-to-br from-background to-secondary/30 border border-border">
            <div className="w-full h-[400px] md:h-[500px]">
              {error || !webGLSupported ? (
                <div className="w-full h-full flex items-center justify-center bg-secondary/20 rounded-2xl">
                  <div className="text-center p-8">
                    <img 
                      src={eap3dModel} 
                      alt={t.altText}
                      className="w-full max-w-md mx-auto mb-6 rounded-lg shadow-lg"
                    />
                    <p className="text-muted-foreground mb-4">
                      {!webGLSupported ? t.noWebGL : t.loadError}
                    </p>
                    {webGLSupported && (
                      <Button onClick={() => setError(false)} variant="outline">
                        <RotateCw className="mr-2 h-4 w-4" />
                        {t.retry}
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <Canvas
                  camera={{ position: [0, 0, 12], fov: 40 }}
                  className="cursor-grab active:cursor-grabbing"
                  onCreated={() => console.log("Canvas created successfully")}
                  gl={{ 
                    antialias: true,
                    alpha: true,
                    preserveDrawingBuffer: true,
                    failIfMajorPerformanceCaveat: false
                  }}
                >
                  <Suspense fallback={
                    <mesh>
                      <boxGeometry args={[1, 1, 1]} />
                      <meshStandardMaterial color="#888" wireframe />
                    </mesh>
                  }>
                    <ambientLight intensity={0.5} />
                    <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} />
                    <pointLight position={[-10, -10, -10]} intensity={0.5} />
                    
                    <PresentationControls
                      global
                      rotation={[0.13, 0.1, 0]}
                      polar={[-Math.PI / 4, Math.PI / 4]}
                      azimuth={[-Math.PI / 4, Math.PI / 4]}
                      config={{ mass: 2, tension: 400 }}
                      snap={{ mass: 4, tension: 400 }}
                    >
                      <Model />
                    </PresentationControls>
                    
                    <Environment preset="studio" />
                    <OrbitControls 
                      enableZoom={true} 
                      enablePan={false}
                      minDistance={8}
                      maxDistance={20}
                    />
                  </Suspense>
                </Canvas>
              )}
            </div>

            {/* Controls Info */}
            <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center bg-card/90 backdrop-blur-sm rounded-lg p-4 border border-border">
              <div className="flex gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <RotateCw className="w-4 h-4" />
                  <span>{t.dragRotate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Maximize2 className="w-4 h-4" />
                  <span>{t.scrollZoom}</span>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                onClick={() => window.open('https://eap-abutments.com/3d-modell-eap-abutment/', '_blank')}
              >
                {t.fullscreen}
              </Button>
            </div>
          </div>

          {/* Features Below 3D Model */}
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div className="text-center p-6 rounded-xl bg-card border border-border">
              <div className="text-3xl font-bold text-primary mb-2">{t.feature1Title}</div>
              <div className="text-sm text-muted-foreground">{t.feature1Text}</div>
            </div>
            <div className="text-center p-6 rounded-xl bg-card border border-border">
              <div className="text-3xl font-bold text-primary mb-2">{t.feature2Title}</div>
              <div className="text-sm text-muted-foreground">{t.feature2Text}</div>
            </div>
            <div className="text-center p-6 rounded-xl bg-card border border-border">
              <div className="text-3xl font-bold text-primary mb-2">{t.feature3Title}</div>
              <div className="text-sm text-muted-foreground">{t.feature3Text}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};